import './styles/buttons.css';
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSignal } from "@preact/signals-react";
import { FaUpload, FaSpinner, FaCog } from "react-icons/fa";
import PageForm from "../../templates/PageForm";
import Tooltip from "../../atoms/Tooltip";
import FileCard from './components/drawee-risk/FileCard';
import FinancialParamsModal from './components/drawee-risk/FinancialParamsModal';
import CreditorSelector from './components/form/CreditorSelector';
import { useDraweeRiskExtraction } from './hooks/data/useDraweeRiskExtraction';

const CONTRACTS_LIST_URL = '/contracts/0/100/id/ASC/s=;';

const CreateDraweeRiskPage = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const apiVersion = useSignal('');

    const {
        files,
        isLoading,
        creditor,
        setCreditor,
        processingCount,
        readyToSaveCount,
        handleFilesSelected,
        handleRemoveFile,
        handleSave,
    } = useDraweeRiskExtraction();

    const [isDragging, setIsDragging] = useState(false);
    const [isParamsModalOpen, setIsParamsModalOpen] = useState(false);

    const onFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles || selectedFiles.length === 0) return;
        handleFilesSelected(selectedFiles);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
    const onDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFilesSelected(e.dataTransfer.files);
        }
    };

    return (
        <PageForm
            pageTitle="Nova Operação - Risco Sacado"
            apiVersion={apiVersion.value}
            isLoading={isLoading}
            breadcrumb={[
                { label: 'Contratos', url: CONTRACTS_LIST_URL },
                { label: 'Risco Sacado', url: '#' },
            ]}
            controlSlot={
                <div className="flex gap-2 items-center justify-end">
                    <Tooltip content="Parâmetros financeiros">
                        <button type="button" onClick={() => setIsParamsModalOpen(true)}
                            className="bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 font-medium rounded text-sm px-3 py-2.5">
                            <FaCog />
                        </button>
                    </Tooltip>
                    <button type="button"
                        onClick={() => navigate(CONTRACTS_LIST_URL)}
                        className="bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 font-medium rounded text-sm px-5 py-2.5">
                        Cancelar
                    </button>
                    <button type="button"
                        onClick={() => handleSave(navigate)}
                        disabled={readyToSaveCount === 0 || processingCount > 0 || !creditor}
                        className="bg-blue-500 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white font-medium rounded text-sm px-5 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed">
                        Salvar Operações ({readyToSaveCount})
                    </button>
                </div>
            }
        >
            <div className="py-6 space-y-6">
                <CreditorSelector
                    value={creditor}
                    onChange={setCreditor}
                    disabled={isLoading}
                />

                <section
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-blue-300 dark:border-blue-600 bg-white dark:bg-slate-800'}`}
                >
                    <FaUpload className="mx-auto text-3xl text-blue-400 dark:text-blue-500 mb-3" />
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Upload de Contratos</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        Arraste e solte arquivos PDF aqui ou clique para selecionar
                    </p>
                    <div className="flex items-center justify-center gap-4">
                        <input ref={fileInputRef} type="file" accept="application/pdf" multiple
                            onChange={onFilesChange} className="hidden" id="pdf-upload-input" />
                        <label htmlFor="pdf-upload-input"
                            className="inline-flex items-center gap-2 bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-400 text-white font-medium rounded text-sm px-5 py-2.5 cursor-pointer">
                            Selecionar PDFs
                        </label>
                        {processingCount > 0 && (
                            <span className="text-sm text-blue-600 dark:text-blue-400 flex items-center gap-1">
                                <FaSpinner className="animate-spin" /> Processando {processingCount} arquivo(s)...
                            </span>
                        )}
                    </div>
                </section>

                {files.length > 0 && (
                    <section className="space-y-3">
                        {files.map(entry => (
                            <FileCard key={entry.id} entry={entry} onRemove={handleRemoveFile} />
                        ))}
                    </section>
                )}
            </div>
            <FinancialParamsModal isOpen={isParamsModalOpen} onClose={() => setIsParamsModalOpen(false)} />
        </PageForm>
    );
};

export default CreateDraweeRiskPage;
